/**
 * Onboarding Admin Review — IN-2.g
 * ────────────────────────────────
 * Pure helpers for the admin handoff surface. Zero writes; every
 * function returns a derived view over the canonical
 * (`OnboardingProvider`) state.
 *
 * Hard rules:
 *   • No mutation. Mutation lives in `sessions.ts` + provider.
 *   • No dispatch / payout / contractor activation.
 *   • Status mapping for kanban cards is DERIVED; we do not introduce
 *     a parallel `OnboardingStatus` source.
 *   • The synthetic `onboarding:<sessionId>` namespace is internal —
 *     callers receive only display-grade strings.
 */

import type {
  OnboardingInviteToken,
  OnboardingSession,
} from "./types";
import type { ComplianceDocument } from "@/data/contractor/compliance/complianceDocuments";
import { getOnboardingDocumentsForSession } from "./documents";
import { getNextStepKey, getOnboardingSteps } from "./stepRegistry";

// ── Kanban derivation ────────────────────────────────────────────

/**
 * Status surfaced on the admin onboarding kanban. Derived from
 * `OnboardingInviteToken.status` + `OnboardingSession.status`.
 *
 * NOTE — this is intentionally a derived enum and NOT identical to the
 * canonical `Contractor.onboardingStatus`. The contractor enum
 * pre-dates IN-2 and remains the source of truth for contractor-row
 * pipeline state. IN-2.g surfaces session-derived cards alongside it
 * but never writes back.
 */
export type OnboardingKanbanStatus =
  | "invited"
  | "application_started"
  | "reupload_requested"
  | "under_review"
  | "approved"
  | "rejected"
  | "expired";

export interface OnboardingKanbanRow {
  id: string;
  kind: "token" | "session";
  status: OnboardingKanbanStatus;
  tokenId: string;
  sessionId?: string;
  contractorId?: string;
  entityType: OnboardingSession["entityType"];
  parentEntityId?: string;
  /** Display label safe to render — never the synthetic id. */
  label: string;
  /** ISO timestamp of the most recent state change. */
  lastTouchedAt: string;
}

function tokenLabel(token: OnboardingInviteToken): string {
  return `${token.entityType} invite · ${token.id.slice(-6)}`;
}

function sessionLabel(session: OnboardingSession): string {
  const name = (session.draft?.name ?? "").trim();
  return name || `${session.entityType} session · ${session.id.slice(-6)}`;
}

function deriveSessionStatus(
  session: OnboardingSession,
): OnboardingKanbanStatus {
  switch (session.status) {
    case "draft":
      return "application_started";
    case "submitted":
      return "under_review";
    case "approved":
      return "approved";
    case "rejected":
      return "rejected";
    case "reupload_requested":
      return "reupload_requested";
    default:
      return "application_started";
  }
}

/**
 * Build derived kanban rows. Sessions take precedence over their
 * source token (a consumed token shows as the session it created).
 * Tokens without a session render as "invited" or "expired".
 *
 * Result is sorted by `lastTouchedAt` descending — freshest first.
 */
export function getOnboardingKanbanRows(
  tokens: OnboardingInviteToken[],
  sessions: OnboardingSession[],
  at: string = new Date().toISOString(),
): OnboardingKanbanRow[] {
  const sessionByTokenId = new Map<string, OnboardingSession>();
  for (const s of sessions) sessionByTokenId.set(s.tokenId, s);

  const rows: OnboardingKanbanRow[] = [];

  for (const t of tokens) {
    const linkedSession = sessionByTokenId.get(t.id);
    if (linkedSession) {
      rows.push({
        id: `session:${linkedSession.id}`,
        kind: "session",
        status: deriveSessionStatus(linkedSession),
        tokenId: t.id,
        sessionId: linkedSession.id,
        contractorId: linkedSession.contractorId,
        entityType: linkedSession.entityType,
        parentEntityId: linkedSession.parentEntityId,
        label: sessionLabel(linkedSession),
        lastTouchedAt: linkedSession.lastTouchedAt,
      });
      continue;
    }

    // Token without a session.
    let status: OnboardingKanbanStatus = "invited";
    if (t.status === "revoked") {
      // Suppress revoked tokens from the kanban — they're not actionable.
      continue;
    }
    if (
      t.status === "expired" ||
      (t.status === "pending" && Date.parse(t.expiresAt) <= Date.parse(at))
    ) {
      status = "expired";
    }
    rows.push({
      id: `token:${t.id}`,
      kind: "token",
      status,
      tokenId: t.id,
      contractorId: t.contractorId,
      entityType: t.entityType,
      parentEntityId: t.parentId,
      label: tokenLabel(t),
      lastTouchedAt: t.createdAt,
    });
  }

  return rows.sort((a, b) =>
    a.lastTouchedAt < b.lastTouchedAt ? 1 : a.lastTouchedAt > b.lastTouchedAt ? -1 : 0,
  );
}

/** Subset of kanban statuses that need admin attention. */
export const ACTIONABLE_KANBAN_STATUSES: OnboardingKanbanStatus[] = [
  "under_review",
  "reupload_requested",
];

// ── Reupload resume helper ───────────────────────────────────────

/**
 * After admin requests reupload, the wizard should land the invitee
 * on the first requested step (not on `review` where they hit
 * "Submit"). Returns the first step key that's still pending
 * reupload AND appears in the entity's step list. Falls back to
 * `session.currentStep` when nothing matches.
 */
export function getReuploadResumeStep(session: OnboardingSession): string {
  if (session.status !== "reupload_requested") return session.currentStep;
  const steps = getOnboardingSteps(session).map((s) => s.key);
  const pending = session.reuploadRequestedSteps ?? [];
  for (const key of steps) {
    if (pending.includes(key)) return key;
  }
  // Defensive: if requested step isn't in registry, advance to first
  // unfinished step.
  const next = getNextStepKey(session, "identity");
  return next ?? session.currentStep ?? "identity";
}

// ── Session document summary for admin inspector ─────────────────

export interface OnboardingSessionDocSummary {
  verified: number;
  pending: number;
  rejected: number;
  total: number;
}

export function getOnboardingSessionDocSummary(
  session: OnboardingSession,
  documents: ComplianceDocument[],
): OnboardingSessionDocSummary {
  const docs = getOnboardingDocumentsForSession(documents, session);
  let verified = 0;
  let pending = 0;
  let rejected = 0;
  for (const d of docs) {
    if (d.rejectedAt) rejected += 1;
    else if (d.verifiedAt) verified += 1;
    else pending += 1;
  }
  return { verified, pending, rejected, total: docs.length };
}

// ── IN-2.h — Stale session detection ─────────────────────────────

/**
 * IN-2.h — Suggests onboarding sessions that have gone cold. Pure
 * derivation; no mutation, no auto-expiry. Admin still chooses
 * whether to nudge, request reupload, or revoke.
 *
 * A session is "stale" when:
 *   • Status is non-terminal (`draft`, `submitted`, `reupload_requested`)
 *   • `lastTouchedAt` is older than `staleAfterDays` (default 14)
 */
export interface StaleOnboardingSession {
  session: OnboardingSession;
  /** Whole days since `lastTouchedAt`. */
  staleDays: number;
}

export function getStaleOnboardingSessions(
  sessions: OnboardingSession[],
  opts: { staleAfterDays?: number; at?: string } = {},
): StaleOnboardingSession[] {
  const days = opts.staleAfterDays ?? 14;
  const nowMs = Date.parse(opts.at ?? new Date().toISOString());
  const thresholdMs = days * 24 * 60 * 60 * 1000;
  const out: StaleOnboardingSession[] = [];
  for (const s of sessions) {
    if (s.status === "approved" || s.status === "rejected") continue;
    const touched = Date.parse(s.lastTouchedAt);
    if (!Number.isFinite(touched)) continue;
    const ageMs = nowMs - touched;
    if (ageMs < thresholdMs) continue;
    out.push({ session: s, staleDays: Math.floor(ageMs / (24 * 60 * 60 * 1000)) });
  }
  return out.sort((a, b) => b.staleDays - a.staleDays);
}
